/** @jsx jsx */
import React, { useState } from 'react';
import { Resources, AppPermissions } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import { dateRender, AttachmentsPreview, Authorize } from '@erecruit/modules';
import {
  jsx,
  A,
  Text,
  Badge,
  styled,
  ant,
  Card,
  Flex,
  Container,
  FlexItem,
} from '@erecruit/ui-kit';
import CredentialDetails from './CredentialDetails';

import { Details, Attachments } from '../../types';

const { FormattedMessage } = Intl;

type CredentialsCardProps = Details &
  Attachments & {
    data: Resources.MatchRequirementProgress;
    handleDetails: Function;
    handleAttachment: Function;
  };

const CredentialCardBadge = styled(Badge)({
  marginTop: 3,
  right: 2,
  ...ant({
    '.badge-dot': {
      width: 4,
      height: 4,
      margin: '0 10px',
    },
  }),
});

const CredentialsCard = ({
  data,
  handleDetails,
  isLoadingDetails,
  details,
  handleAttachment,
  isLoadingAttachments,
  attachments,
}: CredentialsCardProps) => {
  const [isAttachmentVisible, setAttachmentVisibility] = useState<boolean>(
    false,
  );
  const [isDetailsVisible, setDetailsVisibility] = useState<boolean>(false);

  const onClickDetails = (
    id: Resources.MatchRequirementProgress['requirementId'],
  ) => {
    setDetailsVisibility(true);
    handleDetails(id);
  };

  const onClickAttachments = (
    id: Resources.MatchRequirementProgress['requirementId'],
  ) => {
    setAttachmentVisibility(true);
    handleAttachment(id);
  };

  return (
    <React.Fragment>
      <Card>
        <Container
          background="pageSecondary"
          gutterDirection="vertical"
          gutter={2}
        >
          <Flex justify="space-between">
            <Text weight={700}>{data.name}</Text>
            <Text color="bodySecondary">{data.status.name}</Text>
          </Flex>
          <Flex justify="space-between">
            <FlexItem>
              {!!data.attachmentCount && (
                <Authorize
                  permission={AppPermissions.PreviewAndDownloadMatchCredentials}
                >
                  <A
                    inline
                    onClick={() => onClickAttachments(data.requirementId)}
                  >
                    <FormattedMessage
                      id={messageIds.vms.matchRecord.credentials.attachments}
                    />
                  </A>
                  <CredentialCardBadge dot />
                </Authorize>
              )}
              <A inline onClick={() => onClickDetails(data.requirementId)}>
                <FormattedMessage
                  id={messageIds.vms.matchRecord.credentials.details}
                />
              </A>
            </FlexItem>
            <Text color="bodySecondary" align="right">
              Expires:{' '}
              {data.expiresOn ? dateRender(data.expiresOn.toString()) : '/'}
            </Text>
          </Flex>
        </Container>
      </Card>
      {isAttachmentVisible && (
        <AttachmentsPreview
          isVisible={isAttachmentVisible}
          setVisibility={setAttachmentVisibility}
          attachments={attachments}
          isLoading={isLoadingAttachments}
          subtitle={data.name}
        />
      )}
      {isDetailsVisible && (
        <CredentialDetails
          isDetailsVisible={isDetailsVisible}
          isLoadingDetails={isLoadingDetails}
          details={[{ ...(details?.[0] || {}), expiresOn: data.expiresOn }]}
          subtitle={data.name}
          setDetailsVisibility={setDetailsVisibility}
        />
      )}
    </React.Fragment>
  );
};

export default CredentialsCard;
